import React, { useState, useCallback } from "react";
import { useModS } from "../hooks/useModS";
import { toast } from "sonner";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { InputGroup } from "@/components/ui/input-group";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import MaterialIcon from "@/components/ui/MaterialIcon";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ScoringConfigFormValues, ConversationAction, NotificationFrequency, Weekday, ReviewThreshold } from "@/modules/scoring/types";
import {
  scoringConfigFormSchema,
  DEFAULT_SCORING_FORM_VALUES,
  DEFAULT_CONVERSATION_ACTIONS,
  DEFAULT_REVIEW_THRESHOLDS,
  CONVERSATION_ACTION_OPTIONS,
  TIMEZONES,
  TIME_OPTIONS,
  FREQUENCY_OPTIONS,
  WEEKDAY_OPTIONS,
} from "@/modules/scoring/schema";
import { scoringApi } from "@/api/scoring";

interface ScoringSetupProps {
  showHeader?: boolean;
  /** Base heading level for sections. Defaults to 2 (standalone page) or 3 (when showHeader is true). */
  baseHeadingLevel?: 2 | 3;
  onValuesChange?: (values: ScoringConfigFormValues) => void;
  /** Called after a successful save. Use this for navigation (e.g. advancing an onboarding wizard). */
  onSave?: () => void;
  /** Called when the user clicks the back button. When provided, a back button is rendered. */
  onBack?: () => void;
  /** Label for the save button. Defaults to "Save". */
  saveLabel?: string;
  /** When true, pre-fills actions and thresholds with defaults if the workspace has none saved yet. Use during onboarding. */
  prefillDefaults?: boolean;
}

const ScoringSetup: React.FunctionComponent<ScoringSetupProps> = ({
  showHeader = true,
  baseHeadingLevel,
  onValuesChange,
  onSave,
  onBack,
  saveLabel = "Save",
  prefillDefaults = false,
}) => {
  // When showHeader is true (onboarding), the header is h2, so sections start at h3.
  // When showHeader is false (standalone page), the page title is h1, so sections start at h2.
  const sectionLevel = (baseHeadingLevel ?? (showHeader ? 3 : 2)) as 1 | 2 | 3 | 4 | 5 | 6;
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ScoringConfigFormValues>({
    resolver: zodResolver(scoringConfigFormSchema),
    defaultValues: DEFAULT_SCORING_FORM_VALUES,
    mode: "onChange",
  });

  React.useEffect(() => {
    scoringApi.load().then((values) => {
      const actionsEmpty = values.conversationActions.length === 0;
      const thresholdsEmpty = values.reviewThresholds.length === 0;
      const willPrefill = prefillDefaults && (actionsEmpty || thresholdsEmpty);

      reset({
        ...values,
        conversationActions: actionsEmpty && prefillDefaults
          ? DEFAULT_CONVERSATION_ACTIONS
          : values.conversationActions,
        reviewThresholds: thresholdsEmpty && prefillDefaults
          ? DEFAULT_REVIEW_THRESHOLDS
          : values.reviewThresholds,
      });

      if (willPrefill) setDefaultsApplied(true);
    }).catch(() => {
      setLoadError(true);
      toast.error("Failed to load scoring configuration. Your settings may not be up to date.");
    });
  }, [reset, prefillDefaults]);

  const submitForm = useCallback(
    async (values: ScoringConfigFormValues) => {
      setSaving(true);
      try {
        await scoringApi.save(values);
        toast.success("Scoring setup updated");
        onSave?.();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to save scoring configuration");
      } finally {
        setSaving(false);
      }
    },
    [onSave],
  );

  const { fields: actionFields, append: appendAction, remove: removeAction } = useFieldArray({
    control,
    name: "conversationActions",
  });

  const { fields: thresholdFields, append: appendThreshold, remove: removeThreshold } = useFieldArray({
    control,
    name: "reviewThresholds",
  });

  const conversationActions = watch("conversationActions");
  const reviewThresholds = watch("reviewThresholds");
  const notificationFrequency = watch("notificationFrequency");
  const messagingDay = watch("messagingDay");
  const notificationTime = watch("notificationTime");
  const notificationTimezone = watch("notificationTimezone");
   

  // Derive which actions are already used so the dropdown can filter them
  const usedActions = new Set(conversationActions.map((a) => a.action));
  const allActionsAssigned = CONVERSATION_ACTION_OPTIONS.every((opt) =>
    usedActions.has(opt.value),
  );

  // Compute per-row min/max percent bounds based on neighbours.
  // Intentionally not memoised: useFieldArray can mutate the watched array
  // in-place, so a useMemo keyed on the array reference may serve stale bounds.
  const thresholdBounds = reviewThresholds.map((_t: ReviewThreshold, i: number) => {
    const prevPercent = i > 0 ? reviewThresholds[i - 1].percent : -1;
    const nextPercent = i < reviewThresholds.length - 1 ? reviewThresholds[i + 1].percent : 101;
    return { minPercent: prevPercent + 1, maxPercent: nextPercent - 1 };
  });

  const triggerSubmit = useCallback(() => handleSubmit(submitForm)(), [handleSubmit, submitForm]);
  useModS(triggerSubmit, !saving && !loadError);

  // Notify parent of all values when any field changes
  React.useEffect(() => {
    onValuesChange?.({
      conversationActions,
      reviewThresholds,
      notificationFrequency,
      messagingDay,
      notificationTime,
      notificationTimezone,
    });
  }, [
    conversationActions,
    reviewThresholds,
    notificationFrequency,
    messagingDay,
    notificationTime,
    notificationTimezone,
    onValuesChange,
  ]);

  return (
    <div className="space-y-6">
      {showHeader && (
        <div>
          <Heading level={2} variant="md">Your scoring preferences</Heading>
          <Text className="text-muted-foreground mt-1">
            Configure how agent performance is measured and scored.
          </Text>
        </div>
      )}

      {defaultsApplied && (
        <Card className="bg-muted border-0 shadow-none">
          <CardContent>
            <Text variant="sm" className="text-muted-foreground">
              We&apos;ve pre-filled some recommended defaults to get you started. Feel free to adjust them.
            </Text>
          </CardContent>
        </Card>
      )}

      {/* Frequency Section */}
      <div className="space-y-4">
        <Heading level={sectionLevel} variant="sm">Message frequency</Heading>

        {/* Frequency */}
        <div className="flex items-center gap-2 flex-wrap">
          <Text as="span" variant="sm">Send scores</Text>
          <Controller
            name="notificationFrequency"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={(value) => field.onChange(value as NotificationFrequency)}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {notificationFrequency === "weekly" && (
            <>
              <Text as="span" variant="sm">on</Text>
              <Controller
                name="messagingDay"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(value) => field.onChange(value as Weekday)}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEEKDAY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </>
          )}
        </div>

        {/* Timing */}
        <div className="flex items-center gap-2 flex-wrap">
          <Text as="span" variant="sm">Send scores at</Text>
          <Controller
            name="notificationTime"
            control={control}
            render={({ field }) => (
              <Combobox
                value={field.value}
                onValueChange={field.onChange}
                options={TIME_OPTIONS}
                placeholder="Time"
                searchPlaceholder="Search time..."
                className="w-28"
              />
            )}
          />
          <Controller
            name="notificationTimezone"
            control={control}
            render={({ field }) => (
              <Combobox
                value={field.value}
                onValueChange={field.onChange}
                options={[...TIMEZONES]}
                placeholder="Timezone"
                searchPlaceholder="Search timezone..."
                className="w-80"
              />
            )}
          />
        </div>
        {errors.notificationTime && (
          <Text variant="sm" className="text-destructive">
            {errors.notificationTime.message}
          </Text>
        )}
        {errors.notificationTimezone && (
          <Text variant="sm" className="text-destructive">
            {errors.notificationTimezone.message}
          </Text>
        )}
      </div>

      {/* Action Points Section */}
      <div className="space-y-4">
        <Heading level={sectionLevel} variant="sm">Action points</Heading>

        <div className="space-y-3">
          {actionFields.map((field, index) => {
            // Available options: actions not used by other rows, plus the currently selected action for this row
            const currentAction = conversationActions[index]?.action;
            const availableOptions = CONVERSATION_ACTION_OPTIONS.filter(
              (opt) => opt.value === currentAction || !usedActions.has(opt.value),
            );

            return (
              <Card key={field.id} className="bg-transparent shadow-none py-3">
                <CardContent className="flex items-center gap-2 flex-wrap">
                  <Text as="span" variant="sm">Employees score</Text>
                  <Controller
                    name={`conversationActions.${index}.points`}
                    control={control}
                    render={({ field: pointsField }) => (
                      <Input
                        type="number"
                        min={0}
                        className="w-20"
                        {...pointsField}
                        value={pointsField.value}
                        onChange={(e) => pointsField.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                  <Text as="span" variant="sm">points per</Text>
                  <Controller
                    name={`conversationActions.${index}.action`}
                    control={control}
                    render={({ field: actionField }) => (
                      <Select
                        value={actionField.value}
                        onValueChange={(value) => actionField.onChange(value as ConversationAction)}
                      >
                        <SelectTrigger className="w-52">
                          <SelectValue placeholder="Select action" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeAction(index)}
                    aria-label="Remove action"
                    className="ml-auto text-muted-foreground hover:text-destructive"
                  >
                    <MaterialIcon icon="delete" size={18} />
                  </Button>
                </CardContent>
              </Card>
            );
          })}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              // Find the first unused action to pre-select
              const nextAction = CONVERSATION_ACTION_OPTIONS.find(
                (opt) => !usedActions.has(opt.value),
              );
              if (nextAction) {
                appendAction({ action: nextAction.value, points: 5 });
              }
            }}
            disabled={allActionsAssigned}
          >
            <MaterialIcon icon="add" size={18} />
            Add action
          </Button>
        </div>

        {errors.conversationActions && (
          <Text variant="sm" className="text-destructive">
            {typeof errors.conversationActions.message === "string"
              ? errors.conversationActions.message
              : "Invalid conversation actions"}
          </Text>
        )}

      </div>

      {/* Review Points Section */}
      <div className="space-y-4">
        <Heading level={sectionLevel} variant="sm">Review points</Heading>

        {/* Dynamic Review Thresholds */}
        <div className="space-y-3">
          {thresholdFields.map((field, index) => {
            const { minPercent, maxPercent } = thresholdBounds[index] ?? { minPercent: 0, maxPercent: 100 };
            return (
              <Card key={field.id} className="bg-transparent shadow-none py-3">
                <CardContent className="flex items-center gap-2 flex-wrap">
                  <Text as="span" variant="sm">Employees score</Text>
                  <Controller
                    name={`reviewThresholds.${index}.points`}
                    control={control}
                    render={({ field: pointsField }) => (
                      <Input
                        type="number"
                        min={0}
                        className="w-20"
                        {...pointsField}
                        value={pointsField.value}
                        onChange={(e) => pointsField.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                  <Text as="span" variant="sm">points per review when</Text>
                  <Controller
                    name={`reviewThresholds.${index}.percent`}
                    control={control}
                    render={({ field: percentField }) => (
                      <InputGroup
                        type="number"
                        min={minPercent}
                        max={maxPercent}
                        suffix="%"
                        className="w-20"
                        {...percentField}
                        value={percentField.value}
                        onChange={(e) => {
                          const clamped = Math.min(Math.max(Number(e.target.value), minPercent), maxPercent);
                          percentField.onChange(clamped);
                        }}
                      />
                    )}
                  />
                  <Text as="span" variant="sm">of reviews are positive</Text>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center text-muted-foreground cursor-default">
                        <MaterialIcon icon="info" size={16} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Reviews that score 4 or above</TooltipContent>
                  </Tooltip>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeThreshold(index)}
                    aria-label="Remove threshold"
                    className="ml-auto text-muted-foreground hover:text-destructive"
                  >
                    <MaterialIcon icon="delete" size={18} />
                  </Button>
                </CardContent>
              </Card>
            );
          })}

          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const lastThreshold = reviewThresholds[reviewThresholds.length - 1];
              const defaultPoints = lastThreshold ? lastThreshold.points + 10 : 5;
              const highestPercent = reviewThresholds.length > 0
                ? Math.max(...reviewThresholds.map((t: ReviewThreshold) => t.percent))
                : 40;
              appendThreshold({ points: defaultPoints, percent: Math.min(highestPercent + 10, 100) });
            }}
          >
            <MaterialIcon icon="add" size={18} />
            Add threshold
          </Button>
        </div>

        {errors.reviewThresholds && (
          <Text variant="sm" className="text-destructive">
            {typeof errors.reviewThresholds.message === "string"
              ? errors.reviewThresholds.message
              : "Invalid review thresholds"}
          </Text>
        )}
      </div>

      <div className="flex justify-between gap-3 pt-2">
        {onBack ? (
          <Button variant="outline" onClick={onBack} disabled={saving}>
            Back
          </Button>
        ) : (
          <div />
        )}
        <Button onClick={handleSubmit(submitForm)} disabled={saving || loadError}>
          {saving ? "Saving..." : saveLabel}
        </Button>
      </div>
    </div>
  );
};

export default ScoringSetup;
